import type { PumpStatus } from "@tandemdiabetes/sugarmate-api-react-client"
import { PumpType } from "@tandemdiabetes/sugarmate-api-react-client"
import classNames from "classnames"
import type {
  ComponentPropsWithoutRef,
  Ref,
} from "react"
import {
  forwardRef,
  useMemo,
} from "react"

import { isCartridgeFillLow } from "./isCartridgeFillLow"
import styles from "./PumpStatus.module.scss"

import { usePolyglot } from "src/contexts"
import { DataRecency } from "src/types"
import {
  round,
  serverInsulinToUserInsulin,
} from "src/utils"

const localizationPrefix = "components.pump_status.insulin_left."

export interface Props extends ComponentPropsWithoutRef<"div"> {
  pumpStatus: PumpStatus;
  pumpStatusRecency: DataRecency;
}

export const PumpCartridgeFill = forwardRef<HTMLDivElement, Props>(
  function PumpCartridgeFill(props: Props, propRef: Ref<HTMLDivElement>) {
    const {
      pumpStatus,
      pumpStatusRecency,
      ...divProps
    } = props

    const {
      pump_type: pumpType,
      cartridge_fill: cartridgeFill,
    } = pumpStatus

    const polyglot = usePolyglot()

    const cartridgeFillString: string = useMemo(
      () => {
        if (cartridgeFill === null || pumpStatusRecency === DataRecency.Stale) {
          return "---"
        }

        const userCartridgeFill = serverInsulinToUserInsulin(cartridgeFill)

        if (pumpType === PumpType.Mobi && userCartridgeFill === 1) {
          return polyglot.t(`${localizationPrefix}low`)
        }

        return polyglot.t(
          "unit.num_units_abbreviated",
          { units: round(userCartridgeFill).toLocaleString(polyglot.locale()) },
        )
      },
      [
        polyglot,
        cartridgeFill,
        pumpType,
        pumpStatusRecency,
      ],
    )

    const lowFill = isCartridgeFillLow(
      cartridgeFill,
      pumpStatusRecency,
    )

    return (
      <div
        {...divProps}
        className={classNames(
          divProps.className,
          lowFill && styles.lowText,
        )}
        ref={propRef}
      >
        {cartridgeFillString}
      </div>
    )
  },
)
